import Konva from 'konva';
import { Effect } from 'types/Effects';

export const TRANSFORM_PADDING = 12;
export const ANCHOR_WIDTH = 6;
export const ANCHOR_HEIGHT = 20;
export const TRANSFORMER_DEFAULT_ID = 'defaultTransformer';
export const HOVER_BORDER_ID = 'hoverBorder';

const enabledAnchors = [
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
  'middle-right',
  'middle-left'
];

export const createHoverBorder = (targetId: string) => {
  const tr = new Konva.Transformer({
    id: HOVER_BORDER_ID,
    attachedId: targetId,
    borderStroke: '#C209C1',
    resizeEnabled: false,
    rotateEnabled: false,
    borderStrokeWidth: 2,
    padding: TRANSFORM_PADDING,
    enabledAnchors: []
  });
  addBackground(tr);
  return tr;
};

export const customizeAnchorStyle = (transformer: Konva.Transformer) => {
  const middleRight = transformer.findOne<Konva.Node>('.middle-right');
  const middleLeft = transformer.findOne<Konva.Node>('.middle-left');
  middleRight.setAttrs({
    width: ANCHOR_WIDTH,
    height: ANCHOR_HEIGHT,
    anchorCornerRadius: 3,
    offsetX: -(TRANSFORM_PADDING - ANCHOR_WIDTH / 2),
    offsetY: ANCHOR_HEIGHT / 2
  });
  middleLeft.setAttrs({
    width: ANCHOR_WIDTH,
    height: ANCHOR_HEIGHT,
    anchorCornerRadius: 3,
    offsetX: TRANSFORM_PADDING + ANCHOR_WIDTH / 2,
    offsetY: ANCHOR_HEIGHT / 2
  });
};

export const hideAnchorsWhenSmall = (tr: Konva.Transformer, anchorsNames: string[]) => {
  const minSize = 42;
  anchorsNames.forEach(anchorName => {
    const anchor = tr.findOne<Konva.Node>(`.${anchorName}`);
    const opacity = tr.width() < minSize || tr.height() < minSize ? 0 : 100;
    anchor.setAttr('opacity', opacity);
  });
};

export const changeTrBorderStyle = (tr: Konva.Transformer) => {
  const back = tr.findOne<Konva.Shape>('.back');
  if (!back) {
    return;
  }

  const strokeWidth = back.strokeWidth();
  back.sceneFunc((ctx: any, shape) => {
    const color = '#fff';
    ctx.strokeStyle = color;
    ctx.beginPath();
    ctx.lineWidth = strokeWidth;
    ctx.rect(
      strokeWidth - TRANSFORM_PADDING,
      strokeWidth - TRANSFORM_PADDING,
      shape.width() - strokeWidth * 2 + TRANSFORM_PADDING * 2,
      shape.height() - strokeWidth * 2 + TRANSFORM_PADDING * 2
    );
    ctx.stroke();

    ctx.shadowBlur = 0;
    ctx.strokeStyle = color;
    ctx.beginPath();
    ctx.rect(
      -TRANSFORM_PADDING,
      -TRANSFORM_PADDING,
      shape.width() + TRANSFORM_PADDING * 2,
      shape.height() + TRANSFORM_PADDING * 2
    );
    ctx.stroke();

    ctx.fillStrokeShape(shape);
  });
};

export const customizeTransformer = (transformer: Konva.Transformer) => {
  transformer.update = () => {
    Konva.Transformer.prototype.update.apply(transformer);
    customizeAnchorStyle(transformer);
    changeTrBorderStyle(transformer);
    hideAnchorsWhenSmall(transformer, [
      'top-center',
      'top-right',
      'middle-left',
      'bottom-left',
      'bottom-center',
      'bottom-right'
    ]);
  };
  transformer.update();
};

export const addBackground = (transformer: Konva.Transformer) => {
  const back = transformer.findOne<Konva.Shape>('.back');
  back?.fill('#C209C133');
};

export const createTransformer = (konvaLayer: Konva.Layer) => {
  const transformer = new Konva.Transformer({
    id: TRANSFORMER_DEFAULT_ID,
    nodes: [],
    enabledAnchors,
    anchorStroke: '#C209C1',
    anchorFill: '#fff',
    anchorSize: 12,
    borderStroke: '#C209C1',
    anchorCornerRadius: 50,
    padding: TRANSFORM_PADDING,
    resizeEnabled: true,
    rotateEnabled: false,
    borderStrokeWidth: 2,
    anchorStrokeWidth: 2,
    rotateAnchorOffset: 20,
    rotationSnaps: [0, 90, 180, 270],
    rotationSnapTolerance: 10,
    keepRatio: true,
    // visible: false,
    boundBoxFunc(oldBoundBox, newBoundBox) {
      if (
        (newBoundBox.height < 10 && newBoundBox.height < oldBoundBox.height) ||
        (newBoundBox.width < 10 && newBoundBox.width < oldBoundBox.width)
      ) {
        return oldBoundBox;
      }

      return newBoundBox;
    }
  });
  konvaLayer.add(transformer);
  customizeTransformer(transformer);
  return transformer;
};

export const transformHandler = (
  transformer: Konva.Transformer,
  konvaLayer: Konva.Layer,
  transformIds: string[],
  effects: Effect[]
) => () => {
  if (['middle-right', 'middle-left'].includes(transformer.getActiveAnchor())) {
    const { _context: ctx } = konvaLayer.getContext();
    transformIds.forEach((id: string) => {
      const rect = konvaLayer.findOne(`#${id}`);
      const transformWidth = rect.width() * rect.scaleX();
      const effect = effects.find(item => item.id === id);
      const {
        font: { fontFamily },
        fontSize
      } = effect;
      const font = `${fontSize}px ${fontFamily}`;
      ctx.font = font;
      const prevWidth = rect.width();
      rect.setAttrs({
        width: transformWidth,
        scaleX: 1,
        scaleY: 1
      });
      if (rect.height() === 0) {
        rect.setAttr('width', prevWidth);
      }
    });
  }
};

export const transformEndHandler = (
  transformer: Konva.Transformer,
  konvaLayer: Konva.Layer,
  transformIds: string[],
  effects: Effect[],
  updateEffects: (param: Partial<Effect>[]) => void,
  resetStage: () => void
) => () => {
  if (['middle-right', 'middle-left'].includes(transformer.getActiveAnchor())) {
    const params = transformIds.map((id: string) => {
      const rect = konvaLayer.findOne(`#${id}`) as Konva.Text;
      const transformEndWidth = rect.width() * rect.scaleX();
      const effect = effects.find(item => item.id === id);
      const {
        font: { fontFamily },
        fontSize
      } = effect;
      const { _context: ctx } = konvaLayer.getContext();
      const font = `${fontSize}px ${fontFamily}`;

      ctx.font = font;
      return {
        id,
        fontSize,
        position: { x: rect.attrs.x, y: rect.attrs.y },
        textArr: rect.textArr.map(({ text }) => text),
        width: transformEndWidth,
        height: rect.height()
      };
    });
    updateEffects(params);
  } else {
    const params = transformIds.map((id: string) => {
      const rect = konvaLayer.findOne(`#${id}`);
      const { fontSize } = effects.find(item => item.id === id);
      return {
        id,
        fontSize: fontSize * rect.scaleX(),
        position: { x: rect.attrs.x, y: rect.attrs.y },
        width: rect.width() * rect.scaleX(),
        height: rect.height() * rect.scaleX()
      };
    });
    updateEffects(params);
  }
  resetStage();
};
